import { unitPlanFragment } from "AppComponents/Post/modules/PostFragments";
import { plannerFieldFragment } from "UnitPlans/modules/UnitPlanFragments";
import { gql } from "lib/apolloClient";
import {
  resolvedFieldValueFragment,
  toolResponseFragment,
  criteriaSetFragment,
  criteriaValueFragment,
} from "modules/CommonFragments";

export const assignmentFragment = {
  assignmentProgressSummary: gql`
    fragment assignmentProgressSummaryItem on Assignment {
      id
      contentType
      createdAt
      isPrivate
      state {
        state
      }
      isTeacherEvaluationEnabled
      isStudentEvaluationEnabled
      isStudentSubmissionEnabled
      isExcludedFromFinalGradeCalculation
      course {
        id
      }
      deadline
      content {
        id
        ... on Assessment {
          id
          assessmentType {
            id
            value
          }
          title {
            id
            value
          }
          assessmentTools(
            filters: { assessmentToolType: $assessmentToolTypes }
          ) {
            edges {
              node {
                id
                assessmentToolType
                assessmentToolItem {
                  id
                  ... on AcademicCriteriaSet {
                    id
                    academicCriteriaValues {
                      id
                      label
                      scoreValues {
                        id
                        genericTag {
                          id
                          label
                        }
                        minValue
                        maxValue
                      }
                    }
                    criteriaType
                  }
                  ... on MypObjectiveRubric {
                    id
                    label
                    criteria {
                      id
                      highestMark
                    }
                  }

                  ... on ScoreAssessmentTool {
                    id
                    scoreToolData {
                      id
                      maxScore
                    }
                  }
                }
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          templateId
          unitPlan {
            id
            allFields {
              ...unitPlanAllFields
            }
            ...unitPlanItem
          }
          fields(
            uids: [
              "measureAssessing"
              "objectivesMYP"
              "interdisciplinaryCriteriaMYP"
              "subjectLevel"
              "atls"
              "benchmarks"
              "concepts"
              "lp"
              "learningStandardUBD"
              "atlsUBD"
              "learningStandardMYP"
              "atlsMYP"
              "subjectStandardDP"
              "atlDP"
              "learnerProfileDP"
              "syllabusContentDP"
            ]
          ) {
            id
            uid
            value
            resolvedMinimalTree {
              ...resolvedMinimalTreeItem
            }
          }
          category {
            id
            label
            colorCode
          }
          academicTerm {
            id
          }
          weightage
          isLearningGoalsEvaluationEnabled
        }
      }
      assignedStudents {
        id
        student {
          id
          profileImage
          firstName
          middleName
          lastName
          isArchived
          courseMap(filters: $courseMapFilters) {
            tags {
              id
              label
            }
          }
        }
        toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
          id
          toolId
          toolType
          toolResponses {
            ...toolResponseItem
          }
        }
        ibPYPElementRatings {
          id
          value
          ibPYPElementId
          ibPYPElementType
          academicCriteriaValue {
            id
            label
          }
          academicCriteriaSet {
            id
          }
        }
        elementRemarks(filters: { creatorTypes: [STAFF] }) {
          id
          message
          ibPYPElementId
          ibPYPElementType
        }
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        status
        isSubmitted
        lockingState {
          state
        }
      }
      counts {
        totalDraftSubmissions
      }
    }
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${unitPlanFragment.unitPlanDetails}
    ${plannerFieldFragment.unitPlanAllFields}
    ${toolResponseFragment.toolResponse}
  `,
};

export const organizationAssignmentsFragment = {
  organizationAssignmentProgressSummary: gql`
    fragment organizationAssignmentItem on Organization {
      id
      assignments(
        first: $first
        after: $after
        filters: {
          contentTypes: $contentTypes
          courseIds: $courseIds
          isTeacherEvaluationEnabled: $isTeacherEvaluationEnabled
          assessmentToolTypes: $assessmentToolTypes
          assessmentTypes: $assessmentTypes
          isAssessmentScoringEnabled: $isAssessmentScoringEnabled
          searchText: $searchText
          excludedAssessmentTypes: $excludedAssessmentTypes
          academicTermIds: $academicTermIds
          categoryIds: $categoryIds
          groupTypes: $groupTypes
          subjectIds: $subjectIds
        }
      ) {
        edges {
          node {
            ...assignmentProgressSummaryItem
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
    ${assignmentFragment.assignmentProgressSummary}
  `,
};

export const courseFragment = {
  courseDetails: gql`
    fragment courseDetailsItem on Course {
      id
      title
      primaryGrade(filters: $courseGradeFilters) {
        id
        name
      }
      subjects {
        id
      }
      grades(filters: $courseGradeFilters) {
        id
        name
      }
    }
  `,
};

export const studentSubjectSummaryPypRatingFragment = {
  studentElementRating: gql`
    fragment ratingItem on IBPYPElementRating {
      id
      ibPYPElementId
      ibPYPElementType

      academicCriteriaSet {
        ...criteriaSetItem
      }
      academicCriteriaValue {
        ...criteriaValueItem
      }
      parentType
    }
    ${criteriaSetFragment.criteriaSet}
    ${criteriaValueFragment.criteriaValue}
  `,
};

export const studentProgressReportFragments = {
  publishedReportBasicDetails: gql`
    fragment publishedStudentProgressReportItem on PublishedStudentProgressReport {
      id
      studentProgressReport {
        id
        courseProgressReportTask {
          id
          progressReportTaskGroup {
            id
            template {
              id
              gradingPeriodType
            }
          }
          title
          parentEntity {
            ... on Grade {
              id
            }
          }
        }
      }
    }
  `,
};
